import type { ValidationErrors } from '@/features/enrollment'

// Validation function for Step5
export const validateStep5 = (data: any): ValidationErrors => {
  const errors: { [key: string]: string } = {}
  
  // Check for exactly 5-7 gaming disorder symptoms (excluding "None of the above")
  const gamingDisorderSymptoms = data.gamingDisorderSymptoms || []
  const validSymptoms = gamingDisorderSymptoms.filter((symptom: string) => symptom !== 'None of the above')
  
  if (validSymptoms.length < 5) {
    errors.gamingDisorderSymptoms = `Please select exactly 5 gaming disorder symptoms. You have selected ${validSymptoms.length}. (Minimum: 5, Maximum: 7)`
  } else if (validSymptoms.length > 7) {
    errors.gamingDisorderSymptoms = `Please select no more than 7 gaming disorder symptoms. You have selected ${validSymptoms.length}. (Minimum: 5, Maximum: 7)`
  }
  
  // Check if "None of the above" is selected - this will disqualify
  if (gamingDisorderSymptoms.includes('None of the above')) {
    errors.gamingDisorderSymptoms = 'Selecting "None of the above" will disqualify you from this campaign. Please select 5-7 actual symptoms.'
  }
  
  // Check for at least one injury/ailment (excluding "No injury")
  const injuriesAilments = data.injuriesAilments || []
  const validInjuries = injuriesAilments.filter((injury: string) => injury !== 'No injury')
  if (validInjuries.length === 0) {
    errors.injuriesAilments = 'At least one injury/ailment must be selected (excluding "No injury")'
  }
  
  // Check if "No injury" is selected - this will disqualify
  if (injuriesAilments.includes('No injury')) {
    errors.injuriesAilments = 'Selecting "No injury" will disqualify you from this campaign. Please select at least one actual injury/ailment.'
  }
  
  // Check for exactly 5-7 life affected ways (excluding "No affects from video games")
  const lifeAffectedWays = data.lifeAffectedWays || []
  const validWays = lifeAffectedWays.filter((way: string) => way !== 'No affects from video games')
  
  if (validWays.length < 5) {
    errors.lifeAffectedWays = `Please select exactly 5 life affected ways. You have selected ${validWays.length}. (Minimum: 5, Maximum: 7)`
  } else if (validWays.length > 7) {
    errors.lifeAffectedWays = `Please select no more than 7 life affected ways. You have selected ${validWays.length}. (Minimum: 5, Maximum: 7)`
  }
  
  // Check if "No affects from video games" is selected - this will disqualify
  if (lifeAffectedWays.includes('No affects from video games')) {
    errors.lifeAffectedWays = 'Selecting "No affects from video games" will disqualify you from this campaign. Please select 5-7 actual life affects.'
  }
  
  // Check for at least 1 medical treatment (no maximum limit)
  const medicalTreatments = data.medicalTreatments || []
  if (medicalTreatments.length < 1) {
    errors.medicalTreatments = `Please select at least 1 medical treatment. "No treatment" is a permitted answer.`
  }
  if (data.medicalProviderDiagnosis === undefined || data.medicalProviderDiagnosis === null) errors.medicalProviderDiagnosis = 'Medical provider diagnosis status is required'
  
  // If medical provider diagnosis is "Yes", validate medical provider fields
  if (data.medicalProviderDiagnosis === 'Yes') {
    if (!data.medicalProviderName || data.medicalProviderName.trim() === '') errors.medicalProviderName = 'Medical provider name is required when diagnosis is Yes'
    if (!data.medicalProviderAddress1 || data.medicalProviderAddress1.trim() === '') errors.medicalProviderAddress1 = 'Address Line 1 is required when diagnosis is Yes'
    if (!data.medicalProviderCity || data.medicalProviderCity.trim() === '') errors.medicalProviderCity = 'City is required when diagnosis is Yes'
    if (!data.medicalProviderState || data.medicalProviderState.trim() === '') errors.medicalProviderState = 'State is required when diagnosis is Yes'
    if (!data.medicalProviderZip || data.medicalProviderZip.trim() === '') errors.medicalProviderZip = 'ZIP code is required when diagnosis is Yes'
  }
  
  if (data.medicationForGamingInjuries === undefined || data.medicationForGamingInjuries === null) errors.medicationForGamingInjuries = 'Medication for gaming injuries status is required'
  if (data.willingToSpeakToDoctor === undefined || data.willingToSpeakToDoctor === null) errors.willingToSpeakToDoctor = 'Willingness to speak to doctor is required'
  if (data.filedLawsuit === undefined || data.filedLawsuit === null) errors.filedLawsuit = 'Lawsuit filing status is required'
  if (data.convictedMisdemeanorFelony === undefined || data.convictedMisdemeanorFelony === null) errors.convictedMisdemeanorFelony = 'Criminal conviction status is required'
  if (data.receivesSSDI === undefined || data.receivesSSDI === null) errors.receivesSSDI = 'SSDI benefits status is required'
  if (data.receivesSSI === undefined || data.receivesSSI === null) errors.receivesSSI = 'SSI benefits status is required'
  
  return errors
}

// Real-time validation for gaming disorder symptoms
export const validateGamingDisorderSymptoms = (symptoms: string[], errors: ValidationErrors, setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>) => {
  const newErrors = { ...errors }
  
  const validSymptoms = symptoms.filter((symptom: string) => symptom !== 'None of the above')
  
  if (symptoms.includes('None of the above')) {
    newErrors.gamingDisorderSymptoms = 'Selecting "None of the above" will disqualify you from this campaign. Please select 5-7 actual symptoms.'
  } else if (validSymptoms.length < 5) {
    newErrors.gamingDisorderSymptoms = `Please select exactly 5 gaming disorder symptoms. You have selected ${validSymptoms.length}. (Minimum: 5, Maximum: 7)`
  } else if (validSymptoms.length > 7) {
    newErrors.gamingDisorderSymptoms = `Please select no more than 7 gaming disorder symptoms. You have selected ${validSymptoms.length}. (Minimum: 5, Maximum: 7)`
  } else {
    delete newErrors.gamingDisorderSymptoms
  }
  
  setErrors(newErrors)
}

// Real-time validation for life affected ways
export const validateLifeAffectedWays = (ways: string[], errors: ValidationErrors, setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>) => {
  const newErrors = { ...errors }
  
  const validWays = ways.filter((way: string) => way !== 'No affects from video games')
  
  if (ways.includes('No affects from video games')) {
    newErrors.lifeAffectedWays = 'Selecting "No affects from video games" will disqualify you from this campaign. Please select 5-7 actual life affects.'
  } else if (validWays.length < 5) {
    newErrors.lifeAffectedWays = `Please select exactly 5 life affected ways. You have selected ${validWays.length}. (Minimum: 5, Maximum: 7)`
  } else if (validWays.length > 7) {
    newErrors.lifeAffectedWays = `Please select no more than 7 life affected ways. You have selected ${validWays.length}. (Minimum: 5, Maximum: 7)`
  } else {
    delete newErrors.lifeAffectedWays
  }
  
  setErrors(newErrors)
}

// Real-time validation for medical treatments
export const validateMedicalTreatments = (treatments: string[], errors: ValidationErrors, setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>) => {
  const newErrors = { ...errors }
  
  if (treatments.length < 1) {
    newErrors.medicalTreatments = `Please select at least 1 medical treatment. "No treatment" is a permitted answer.`
  } else {
    delete newErrors.medicalTreatments
  }
  
  setErrors(newErrors)
}

// Gaming disorder symptoms options
export const gamingDisorderSymptomsOptions: string[] = [
  'Poor school or work performance due to playing video games',
  'Withdrawal symptoms when games are taken away such as Sadness, Anxiety or Restlessness, Gamers Rage',
  'Loss of interest in sports, hobbies or spending time with family',
  'Gamer recognizes they have a problem with excessive gaming',
  'Deceives family about gameplay time or steals money to play games',
  'Needs to play more hours on video games to the detriment of education or work',
  'Decline in personal hygiene due to gaming',
  'Failed attempts to stop playing video games',
  'Uses video games to relieve negative moods such as guilt or depression',
  'None of the above'
]

// Gaming injuries options
export const gamingInjuriesOptions: string[] = [
  'Diagnosed gaming disorder or addiction',
  'Opposition defiant disorder (ODD)',
  'Suicide attempt',
  'Depression',
  'ADD/ ADHD (Attention deficit hyperactivity disorder)',
  'Gamer\'s rage',
  'Gamer\'s thumb',
  'Seizures',
  'Computer vision syndrome',
  'Carpal tunnel syndrome',
  'Orthopedic injury',
  'Sleep Deprivation Psychosis',
  'Other injury',
  'No injury'
]

// Life affected ways options
export const lifeAffectedWaysOptions: string[] = [
  'Drop in grades',
  'Dropout of school',
  'Hiding or lying about game playing time',
  'Inability to stop playing games',
  'Poor Hygiene due to excessive video gaming',
  'Received an individualized Education Plan (IEP)',
  'Social isolation',
  'Stealing money for gaming',
  'Withdrawal symptoms',
  'Other Affects',
  'No affects from video games'
]

// Medical treatments options
export const medicalTreatmentsOptions: string[] = [
  'Counseling',
  'Doctor visits',
  'Hospitalization',
  'In person gaming addiction program',
  'Online gaming addiction program',
  'Therapy',
  'Other treatment',
  'No treatment'
]
